import { round } from "@repo/system";
import type {
  CO2Rating,
  NutritionServingBasis,
  NutritionalInformation
} from "~/api/products/types";

// Re-exported so the nutrition-climate components can keep importing these from
// this module; the canonical definitions live in the API layer (non-UI).
export type { CO2Rating, NutritionServingBasis, NutritionalInformation };

export const CO2_RATINGS = [
  "A",
  "B",
  "C",
  "D",
  "E"
] as const satisfies readonly CO2Rating[];

/**
 * Tint (cell background) and text (label/foreground) colour for each climate
 * grade, A (best) → E (worst). Data-driven (selected by `rating` at runtime),
 * so applied via inline styles using the CO₂ design tokens in src/index.css.
 */
export const CO2_GRADE_COLORS: Record<
  CO2Rating,
  { tint: string; text: string }
> = {
  A: {
    tint: "rgb(var(--kiosk-co2-a-background))",
    text: "rgb(var(--kiosk-co2-a-text))"
  },
  B: {
    tint: "rgb(var(--kiosk-co2-b-background))",
    text: "rgb(var(--kiosk-co2-b-text))"
  },
  C: {
    tint: "rgb(var(--kiosk-co2-c-background))",
    text: "rgb(var(--kiosk-co2-c-text))"
  },
  D: {
    tint: "rgb(var(--kiosk-co2-d-background))",
    text: "rgb(var(--kiosk-co2-d-text))"
  },
  E: {
    tint: "rgb(var(--kiosk-co2-e-background))",
    text: "rgb(var(--kiosk-co2-e-text))"
  }
};

/**
 * Converts a nutrition value between serving bases using the serving size.
 * Mirrors `calculateNutritionValueFromServingSize` in @kanpla/ordering: without
 * a serving size no conversion is possible, so the value is returned unchanged.
 */
export const makeNutritionConverter = ({
  from,
  to,
  servingSize
}: {
  from: NutritionServingBasis;
  to: NutritionServingBasis;
  servingSize?: number | null;
}) => {
  return (value: number) => {
    if (!servingSize) return value;
    if (from === to) return value;
    if (from === "100g") return value * (servingSize / 100);
    return value / (servingSize / 100);
  };
};

/** Keys on the nutrition payload that are metadata, not nutrient values. */
const NUTRITION_METADATA_KEYS = new Set(["servingSize", "servingBasis"]);

/**
 * Whether the payload carries at least one real nutrient value. `servingBasis`
 * and `servingSize` are metadata, not values — a payload with only those (every
 * nutrient null) counts as empty, so the UI shows its empty state instead of
 * blank macro chips.
 */
export const hasNutritionValues = (
  nutrition: NutritionalInformation | null | undefined
): nutrition is NutritionalInformation =>
  Object.entries(nutrition ?? {}).some(
    ([key, value]) =>
      !NUTRITION_METADATA_KEYS.has(key) && typeof value === "number"
  );

/**
 * Whether the product has anything to show in the Nutrition & climate section:
 * real nutrient values or a climate grade.
 */
export const hasNutritionClimate = (
  nutrition: NutritionalInformation | null | undefined,
  co2Rating: CO2Rating | null | undefined
): boolean => hasNutritionValues(nutrition) || Boolean(co2Rating);

export type MacroKey = "energy" | "protein" | "carbohydrates" | "fat";

export type MacroChipData = {
  key: MacroKey;
  /** Numeric value, or `null` when the field is absent on the nutrition data. */
  value: number | null;
  /** Unit suffix appended to the value, e.g. "g". Empty for energy/kcal. */
  unit: string;
};

/**
 * The four macro chips rendered next to the CO₂ mark, in the order required by
 * the design: calories, protein, carbs, fat. Values are converted to the given
 * `basis` and rounded. Always returns four entries so the metric row keeps its
 * fixed "5 metrics" shape; a missing field renders as a placeholder.
 */
export const getMacroChips = (
  nutrition: NutritionalInformation,
  basis: NutritionServingBasis
): MacroChipData[] => {
  const convert = makeNutritionConverter({
    from: nutrition.servingBasis,
    to: basis,
    servingSize: nutrition.servingSize
  });
  const scale = (value: number | null | undefined) =>
    typeof value === "number" ? round(convert(value), 2) : null;

  return [
    { key: "energy", unit: "", value: scale(nutrition.energy) },
    { key: "protein", unit: "g", value: scale(nutrition.protein) },
    { key: "carbohydrates", unit: "g", value: scale(nutrition.carbohydrates) },
    { key: "fat", unit: "g", value: scale(nutrition.fat) }
  ];
};
